/**
 * Détection d'une nouvelle version déployée de totadmin.
 *
 * Le web est déjà « OTA » : `index.html` est servi en `no-cache` (`kube/nginx.conf`) et Vite hashe
 * le nom des assets, donc un simple rechargement suffit à prendre la dernière version. Le seul
 * angle mort d'un back-office, c'est l'onglet laissé ouvert plusieurs jours : il continue de
 * tourner sur le bundle chargé au démarrage, sans jamais le savoir.
 *
 * La source de vérité est donc `index.html` lui-même : les assets qu'il référence sont exactement
 * ceux que le navigateur chargerait s'il rechargeait maintenant. Rien à générer au build, rien à
 * ajouter côté nginx.
 */

/** Chemin d'un asset hashé par Vite, tel qu'il apparaît dans `index.html` (`/assets/index-DzM5Ivfu.js`). */
const HASHED_ASSET_PATH = /^\/assets\/[\w.-]+\.(?:js|css)$/;

/**
 * Même motif, en global, pour ratisser le HTML servi. Volontairement séparé du précédent : une
 * regex `/g` partagée garderait son `lastIndex` d'un `.test()` à l'autre et ne répondrait juste
 * qu'une fois sur deux.
 */
const HASHED_ASSET_REFERENCE = /\/assets\/[\w.-]+\.(?:js|css)/g;

/** Laps de temps entre les deux lectures d'`index.html` (cf. `hasNewVersionDeployed`). */
const CONFIRMATION_DELAY_MS = 3000;

function sortedUnique(values: string[]): string[] {
    return [...new Set(values)].sort();
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

/** Assets hashés que CE document a chargés au démarrage. */
function readLoadedAssets(): string[] {
    const nodes = document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src], link[href]');

    const paths = [...nodes].map((node) => {
        const url = node instanceof HTMLScriptElement ? node.src : node.href;

        try {
            return new URL(url, window.location.href).pathname;
        } catch {
            return '';
        }
    });

    return sortedUnique(paths.filter((path) => HASHED_ASSET_PATH.test(path)));
}

/**
 * Figé au chargement du module : c'est l'empreinte de la version que l'utilisateur exécute.
 * Vide en dev (Vite sert `/src/main.tsx`, pas d'assets hashés) — c'est ce qui désactive tout.
 */
const LOADED_ASSETS = readLoadedAssets();

/** Assets hashés référencés par un `index.html` servi. */
export function extractAssetsFromHtml(html: string): string[] {
    return sortedUnique(html.match(HASHED_ASSET_REFERENCE) ?? []);
}

/**
 * Rien à surveiller en dev (pas de bundle hashé) ni sous Playwright, où un fetch de fond
 * n'apporterait que du non-déterminisme.
 */
export function isVersionCheckSupported(): boolean {
    return LOADED_ASSETS.length > 0 && window.__TOTENV__.ENV !== 'playwright';
}

async function fetchDeployedAssets(): Promise<string[] | null> {
    try {
        const response = await fetch('/index.html', { cache: 'no-store' });

        if (!response.ok) {
            return null;
        }

        const assets = extractAssetsFromHtml(await response.text());

        return assets.length > 0 ? assets : null;
    } catch {
        // Coupure réseau, VPN, pod en cours de redémarrage : on ne sait pas, donc on ne dit rien.
        // Surtout pas de toast ni de Sentry ici, ce chemin tourne en fond toutes les 5 minutes.
        return null;
    }
}

function isCurrentVersion(assets: string[]): boolean {
    return assets.join('|') === LOADED_ASSETS.join('|');
}

/**
 * `true` si le serveur sert désormais d'autres assets que ceux de cet onglet.
 *
 * La réponse est confirmée par une seconde lecture : pendant un rolling update les deux replicas
 * nginx servent des versions différentes, et un onglet qui vient de charger la nouvelle peut
 * tomber sur l'ancien pod et se croire périmé à tort. Le décalage fait retomber l'essentiel de ces
 * faux positifs, et la fenêtre se referme d'elle-même à la fin du rollout.
 */
export async function hasNewVersionDeployed(): Promise<boolean> {
    const assets = await fetchDeployedAssets();

    if (!assets || isCurrentVersion(assets)) {
        return false;
    }

    await wait(CONFIRMATION_DELAY_MS);

    const confirmation = await fetchDeployedAssets();

    return confirmation !== null && !isCurrentVersion(confirmation);
}

/**
 * Garde-fou du rechargement automatique : a-t-on la moindre raison de croire que l'utilisateur a
 * commencé quelque chose dans cet onglet ?
 *
 * Comparer les champs du DOM à leur `defaultValue` ne marche PAS ici : React réécrit l'attribut
 * `value` des champs contrôlés à chaque rendu, donc `defaultValue` suit toujours la valeur
 * courante et rien n'apparaît jamais modifié (vérifié sur l'écran de connexion). On écoute donc la
 * saisie réelle, ce qui a l'avantage d'être indépendant de la lib de formulaire, et de couvrir
 * aussi bien les `select` que les zones `contenteditable`.
 */
let hasUserTypedInThisTab = false;

function rememberUserInput(event: Event): void {
    // `isTrusted` distingue un vrai geste utilisateur des événements qu'une lib émet pour
    // synchroniser un champ contrôlé — seuls les premiers valent une saisie à préserver.
    if (event.isTrusted) {
        hasUserTypedInThisTab = true;
    }
}

/** Branche l'écoute de la saisie ; renvoie de quoi la débrancher. */
export function watchUserInput(): () => void {
    // En phase de capture : un composant qui arrête la propagation ne doit pas nous rendre aveugles.
    document.addEventListener('input', rememberUserInput, true);
    document.addEventListener('change', rememberUserInput, true);

    return () => {
        document.removeEventListener('input', rememberUserInput, true);
        document.removeEventListener('change', rememberUserInput, true);
    };
}

/**
 * `true` dès que l'utilisateur a saisi quoi que ce soit depuis le chargement de la page.
 *
 * Volontairement sans péremption ni remise à zéro : on ne sait pas si cette saisie a été
 * enregistrée, et se tromper dans ce sens ne coûte qu'un bandeau à cliquer, alors que l'inverse
 * fait perdre son travail à l'utilisateur.
 */
export function hasUserTyped(): boolean {
    return hasUserTypedInThisTab;
}
